import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { getRole } from "../../auth/roleService";
import { menuByRole } from "../../config/menus";

const Sidebar = ({ collapsed, onToggle, isMobileViewport = false, onRequestClose }) => {
  const location = useLocation();
  const role = getRole();
  const menuItems = menuByRole[role] || [];

  const [openMenu, setOpenMenu] = useState(null);

  /** When a child route is active, parent remembers "user collapsed" until they expand again. */
  const MANUAL_CLOSE = "mc:";

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const isChildActive = (children) => {
    return children?.some((child) => isActive(child.path));
  };

  useEffect(() => {
    const path = location.pathname;
    setOpenMenu((prev) => {
      if (typeof prev !== "string" || !prev.startsWith(MANUAL_CLOSE)) return prev;
      const label = prev.slice(MANUAL_CLOSE.length);
      const parent = menuItems.find((i) => i.label === label && i.children);
      if (!parent) return prev;
      const stillUnder = parent.children.some(
        (c) => path === c.path || path.startsWith(`${c.path}/`)
      );
      if (!stillUnder) return null;
      return prev;
    });
  }, [location.pathname, menuItems]);

  const isSubmenuOpen = (item) => {
    const label = item.label;
    const activeChild = isChildActive(item.children);
    if (openMenu === `${MANUAL_CLOSE}${label}`) return false;
    if (openMenu === label) return true;
    return activeChild;
  };

  const toggleMenu = (item) => {
    const label = item.label;
    const activeChild = isChildActive(item.children);
    const showing =
      openMenu === label || (activeChild && openMenu !== `${MANUAL_CLOSE}${label}`);
    if (showing) {
      setOpenMenu(activeChild ? `${MANUAL_CLOSE}${label}` : null);
    } else {
      setOpenMenu(label);
    }
  };

  const renderMenuItem = (item) => {

    // MENU WITH CHILDREN
    if (item.children) {
      const activeChild = isChildActive(item.children);
      const isOpen = isSubmenuOpen(item);

      return (
        <li key={item.label} className={`nav-item has-sub ${isOpen ? "open" : ""}`}>

          <div
            role="button"
            tabIndex={0}
            className={`nav-link ${activeChild ? "active" : ""}`}
            aria-expanded={isOpen}
            onClick={() => toggleMenu(item)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleMenu(item);
              }
            }}
          >
            <i className={item.icon}></i>
            <span>{item.label}</span>
            <i className={`fas fa-angle-right nav-arrow ${isOpen ? "rotate" : ""}`}></i>
          </div>

          {isOpen && (
            <ul className="submenu">
              {item.children.map((child) => {
                const childActive = isActive(child.path);
                return (
                  <li key={child.path} className="nav-sub-item">
                    <Link
                      to={child.path}
                      className={`nav-sublink ${childActive ? "active" : ""}`}
                      aria-current={childActive ? "page" : undefined}
                      onClick={() => isMobileViewport && onRequestClose?.()}
                    >
                      <span className="nav-sublink__text">{child.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}

        </li>
      );
    }

    // NORMAL MENU
    return (
      <li key={item.path} className="nav-item">
        <Link
          to={item.path}
          className={`nav-link ${isActive(item.path) ? "active" : ""}`}
          onClick={() => isMobileViewport && onRequestClose?.()}
        >
          <i className={item.icon}></i>
          <span>{item.label}</span>
        </Link>
      </li>
    );
  };

  const mainItems = menuItems.filter((item) => item.section === "main");
  const pageItems = menuItems.filter((item) => item.section === "pages");

  return (
    <aside
      className={`dashboard-sidebar ${collapsed ? "collapsed" : ""}${isMobileViewport ? " mobile-drawer" : ""}`}
    >

      <div className="sidebar-header">
        <div className="sidebar-logo">
          <img
            src="/assets/images/fortrade_logo.png"
            alt="BSB FORTRADE"
            className="sidebar-logo-img"
          />
        </div>

        <button
          type="button"
          className="sidebar-toggle"
          onClick={onToggle}
          aria-label={isMobileViewport && collapsed ? "Close menu" : "Toggle sidebar"}
        >
          <i
            className={
              isMobileViewport && collapsed ? "fas fa-times" : "fas fa-angle-double-left"
            }
          ></i>
        </button>
      </div>

      <nav className="sidebar-nav">
        <ul className="nav-menu-list">

          {mainItems.map(renderMenuItem)}

          {pageItems.length > 0 && (
            <>
              <li className="nav-section">
                <span className="nav-section-title">PAGES</span>
              </li>

              {pageItems.map(renderMenuItem)}
            </>
          )}

        </ul>
      </nav>

    </aside>
  );
};

export default Sidebar;